import React, { useMemo, useState } from 'react';
import { Input, Message, SelectPicker } from 'rsuite';
import { Button, Modal, useLastPresent } from '@nsi-advisory-services/components';
import ExpenseCardList from './ExpenseCardList';
import { describeExpenseGroup } from '../../utils/expenseGroups';
import { getCategoryLabel } from '../../utils/expenseCategories';

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

// The contents of one batch. Used on both sides of the workflow: the employee
// gets Add/Submit actions, the approver gets the same cards wired to the review
// modal, which is why the actions are props rather than baked in.
export default function ExpenseGroupModal({
  open,
  onClose,
  group: groupProp,
  onOpenExpense,
  onAddExpense,
  onAddExistingExpense,
  onRenameGroup,
  renaming = false,
  // Loose expenses that could join this batch. The page decides what qualifies;
  // this only has to offer them.
  addableExpenses = [],
  addingExisting = false,
  onSubmitGroup,
  submitting = false,
  showApprovalDetails = false,
  error,
}) {
  // The picker opens in place rather than in a second modal, so the group stays in view
  // while an expense is chosen.
  const [picking, setPicking] = useState(false);
  const [pickedId, setPickedId] = useState(null);
  const [renameDraft, setRenameDraft] = useState(null);
  const group = useLastPresent(groupProp); // keeps the dialog steady while it fades out

  // Cleared once the dialog has faded out, not while it is still on screen.
  const resetLocalState = () => {
    setPicking(false);
    setPickedId(null);
    setRenameDraft(null);
  };

  const addableOptions = useMemo(() => addableExpenses.map((expense) => {
    const parts = [
      getCategoryLabel(expense.category) || 'Uncategorized',
      expense.vendor_name,
    ].filter(Boolean);

    return {
      label: `${formatCurrency(expense.amount)} · ${parts.join(' · ')}`,
      value: expense.id,
      description: expense.description || '',
    };
  }), [addableExpenses]);

  if (!group) return null;

  const canSubmit = !group.isSubmitted && group.count > 0;

  // null means "not editing"; an empty string is a name being cleared, which is
  // a different thing and has to survive the difference.
  const isRenaming = renameDraft !== null;
  const trimmedRename = String(renameDraft || '').trim();
  const canSaveRename = trimmedRename.length > 0 && trimmedRename !== group.name;

  // Awaited so the field holds its loading state until the rename lands, rather
  // than snapping shut onto the old name while the write is still in flight.
  const saveRename = async () => {
    if (!canSaveRename || renaming) return;
    await onRenameGroup?.(group, trimmedRename);
    setRenameDraft(null);
  };

  const confirmAdd = () => {
    const expense = addableExpenses.find((row) => String(row.id) === String(pickedId));
    if (!expense) return;
    onAddExistingExpense?.(expense);
    setPickedId(null);
    setPicking(false);
  };

  return (
    <Modal open={open} onClose={onClose} onExited={resetLocalState} size="lg">
      <Modal.Header>
        <Modal.Title>
          {isRenaming ? (
            // Sits in place of the title rather than in a dialog of its own: a
            // rename is a correction, not a step of its own.
            <div className="d-flex align-items-center gap-2 flex-wrap" style={{ paddingRight: 24 }}>
              <Input
                value={renameDraft}
                onChange={setRenameDraft}
                disabled={renaming}
                autoFocus
                placeholder="Group name"
                onPressEnter={saveRename}
                style={{ flex: '1 1 220px', minWidth: 0 }}
              />
              <Button size="sm" onClick={() => setRenameDraft(null)} disabled={renaming}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={saveRename}
                disabled={!canSaveRename}
                loading={renaming}
              >
                Save
              </Button>
            </div>
          ) : (
            <span className="d-inline-flex align-items-center gap-2">
              {group.name}
              {onRenameGroup ? (
                <Button
                  size="sm"
                  icon="pencil"
                  onClick={() => setRenameDraft(group.name || '')}
                  title="Rename this group"
                  aria-label="Rename this group"
                />
              ) : null}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error ? (
          <Message type="error" showIcon header="Error" style={{ marginBottom: 12 }}>
            {String(error?.message || error)}
          </Message>
        ) : null}

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <span className="text-muted">
            {group.count} expense{group.count === 1 ? '' : 's'} • {describeExpenseGroup(group)}
          </span>
          <span className="fw-semibold">Group total: {formatCurrency(group.total)}</span>
        </div>

        {!group.isSubmitted ? (
          <Message type="info" showIcon style={{ marginBottom: 12 }}>
            This group hasn't been submitted yet, so nobody can review it. Submit the group to
            send every expense in it for approval at once.
          </Message>
        ) : null}

        <ExpenseCardList
          expenses={group.expenses}
          onView={onOpenExpense}
          showApprovalDetails={showApprovalDetails}
        />

        {picking ? (
          <div className="mt-3 pt-3 border-top d-flex flex-wrap align-items-center gap-2">
            <div style={{ flex: '1 1 320px', minWidth: 0 }}>
              <SelectPicker
                data={addableOptions}
                value={pickedId}
                onChange={setPickedId}
                block
                cleanable={false}
                disabled={addingExisting}
                // A bounded, overflow-aware menu settles its position in one
                // pass. An unbounded one grows past the viewport, gets pushed
                // back, and re-measures — which is the churn behind the
                // ResizeObserver notice this picker used to raise.
                menuMaxHeight={220}
                preventOverflow
                placeholder={addableOptions.length === 0
                  ? 'No expenses available to add'
                  : 'Select an expense'}
                renderMenuItem={(label, item) => (
                  <div>
                    <div style={{ fontWeight: 600 }}>{label}</div>
                    {item.description ? (
                      <div className="text-muted" style={{ fontSize: 12 }}>{item.description}</div>
                    ) : null}
                  </div>
                )}
              />
            </div>
            <Button
              onClick={() => { setPicking(false); setPickedId(null); }}
              disabled={addingExisting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={confirmAdd}
              disabled={!pickedId || addingExisting}
              loading={addingExisting}
            >
              Add to group
            </Button>
          </div>
        ) : null}

        {picking && addableOptions.length === 0 ? (
          <div className="text-muted mt-2" style={{ fontSize: 13 }}>
            Every other expense is either already in a group or has been decided, and a
            decided expense can no longer be moved.
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer
        left={onAddExistingExpense || onAddExpense ? (
          <>
            {onAddExistingExpense && !picking ? (
              <Button variant="accent" icon="folder-symlink" onClick={() => setPicking(true)} disabled={submitting}>
                Add existing expense to group
              </Button>
            ) : null}
            {onAddExpense ? (
              <Button variant="accent" icon="plus-lg" onClick={() => onAddExpense(group)} disabled={submitting}>
                Add expense
              </Button>
            ) : null}
          </>
        ) : null}
      >
        <Button onClick={onClose}>Close</Button>
        {onSubmitGroup && canSubmit ? (
          <Button
            variant="primary"
            onClick={() => onSubmitGroup(group)}
            loading={submitting}
          >
            Submit all {group.count} expense{group.count === 1 ? '' : 's'}
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}
